import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons, ZiroStackedIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import { Observable, of, Subscription } from 'rxjs';
import { EndUserSearchResource } from '../../../../shared/resources/end-user-search.resource';
import { EndUserRef, EndUserResult } from '../../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { sortBy } from 'lodash';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

export enum ReassignTypes {
  PUBLIC_TO_USER = 'PUBLIC_TO_USER',
  USER_TO_USER = 'USER_TO_USER',
}

export interface ReassignSearch {
  searchTerm: string;
}

interface ReassignUserSearchRow extends DatatableRow {
  displayName: string;
  userId: string;
}

@Component({
  selector: 'ziro-reassign-user-search',
  templateUrl: './reassign-user-search.component.html',
  styleUrls: ['./reassign-user-search.component.scss'],
  standalone: false,
})
export class ReassignUserSearchComponent
  extends ZiroFormAbstractDirective<ReassignSearch>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _endUserSearchResource = inject(EndUserSearchResource);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  readonly reassignType = input<ReassignTypes>();
  readonly serverId = input<number>();
  columns: DatatableColumn<ReassignUserSearchRow>[];
  tableRows: ReassignUserSearchRow[] = [];
  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  isLoading = true;
  ZiroStackedIcons = ZiroStackedIcons;

  formConfig = {
    fields: {
      searchTerm: {
        dataAutomation: 'reassign-search-input',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          placeholder: 'tkey;global.search.placeholder.text',
        }),
      },
    },
  } as ZiroFormConfig;

  protected readonly ReassignTypes = ReassignTypes;
  private _userSearchResults: EndUserResult[];
  private _subscriptions = new Subscription();
  private username: string;

  ngOnInit(): void {
    const routeSub = this._route.queryParams.subscribe((params) => {
      this.username = this._route.snapshot.params.username;
      if (!this.formData.searchTerm && params.search) {
        this.entitySource.next({ searchTerm: params.search });
        this.formData.searchTerm = params.search;
        this.searchForUser();
        this.isSubmitting = false;
      } else if (params.search) {
        this.searchForUser();
      }
      this.isLoading = false;
    });
    this._subscriptions.add(routeSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onSearchClicked(): void {
    this._router
      .navigate([], {
        queryParams: {
          search: this.formData.searchTerm,
        },
      })
      .then(() => this.searchForUser());
  }

  searchForUser(): void {
    this.isSubmitting = true;
    this._endUserSearchResource.searchByQ(this.formData.searchTerm, this.serverId(), 100).subscribe((results) => {
      this._userSearchResults = sortBy(results, (userResult) => userResult.ref.username.toLowerCase());
      this.isSubmitting = false;
      this._createTable();
      this._generateTableRows();
    });
  }

  routeToReassignUser(endUserRef: EndUserRef): void {
    this._ziroFormStateService.setIsFormDirty(false);
    this._router.navigate([`${endUserRef.username}`], { relativeTo: this._route, queryParams: {} });
  }

  onCancelClicked(): void {
    this._router.navigate(['../'], { relativeTo: this._route, replaceUrl: false });
  }

  onEnterSearchPressed(): void {
    if (this.formData.searchTerm?.length > 1 && !this.isSubmitting) {
      this.searchForUser();
    }
  }

  protected submit(): Observable<any> {
    return of(null);
  }

  private _createTable(): void {
    this.columns = [
      {
        name: 'displayName',
        label: 'tkey;pages.details.transfer.to.user.search.display.name',
        columnCharacterWidth: 40,
      },
      {
        name: 'userId',
        label: 'tkey;pages.details.transfer.to.user.search.userid',
        columnCharacterWidth: 60,
      },
    ];
  }

  private _generateTableRows(): void {
    this.tableRows = this._userSearchResults.map(
      (endUserResult): ReassignUserSearchRow => ({
        displayName: `${endUserResult.ref.firstName} ${endUserResult.ref.lastName}`,
        userId: endUserResult.ref.username,
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'reassign-user-select',
            icon: ZiroIcons.NEXT,
            onClick: () => this.routeToReassignUser(endUserResult.ref),
            disabled: this.username === endUserResult.ref.username,
            disabledTooltip: 'tkey;pages.details.transfer.to.user.has.services.text',
          },
        ],
      }),
    );
  }
}
